// src/utils/platform.js

/**
 * Platform / capability checks.
 * Keep these small and predictable, they get used by hooks and UI.
 */

export function isBrowser() {
  return typeof window !== "undefined" && typeof document !== "undefined";
}

export function isSecureContextSafe() {
  // Camera APIs require secure context (https) in modern browsers.
  // localhost is treated as secure by most browsers.
  if (!isBrowser()) return false;
  return Boolean(window.isSecureContext);
}

export function isMobileUserAgent() {
  if (!isBrowser()) return false;
  const ua = navigator.userAgent || "";
  // Broad match for mobile devices. This is not perfect, but good enough for gating UI.
  return /Android|iPhone|iPad|iPod|Mobile|IEMobile|Silk/i.test(ua);
}

export function isIOS() {
  if (!isBrowser()) return false;
  const ua = navigator.userAgent || "";
  // iPadOS 13+ can present as Mac, so also check for touch points.
  const isIPhoneIPadIPod = /iPhone|iPad|iPod/i.test(ua);
  const isIPadOS13Plus =
    /Macintosh/i.test(ua) && typeof navigator.maxTouchPoints === "number" && navigator.maxTouchPoints > 1;
  return isIPhoneIPadIPod || isIPadOS13Plus;
}

export function isAndroidChrome() {
  if (!isBrowser()) return false;
  const ua = navigator.userAgent || "";
  const isAndroid = /Android/i.test(ua);
  const isChrome = /Chrome/i.test(ua);
  const isExcludedChromiumShell = /EdgA|OPR|SamsungBrowser|DuckDuckGo/i.test(ua);
  return isAndroid && isChrome && !isExcludedChromiumShell;
}

export function isPwaInstalled() {
  if (!isBrowser()) return false;
  const isStandalone = window.matchMedia?.("(display-mode: standalone)")?.matches === true;
  const isIosStandalone = window.navigator.standalone === true;
  return isStandalone || isIosStandalone;
}

export function isSafari() {
  if (!isBrowser()) return false;
  const ua = navigator.userAgent || "";
  // Exclude Chrome/Edge/Firefox iOS variants that include "Safari"
  const isSafariLike = /Safari/i.test(ua);
  const isOther =
    /CriOS|FxiOS|EdgiOS|OPiOS/i.test(ua) || /Chrome|Chromium|Edg/i.test(ua);
  return isSafariLike && !isOther;
}

export function isIOSSafari() {
  return isIOS() && isSafari();
}

export function hasGetUserMedia() {
  if (!isBrowser()) return false;
  return Boolean(navigator.mediaDevices && navigator.mediaDevices.getUserMedia);
}

export function hasGeolocation() {
  if (!isBrowser()) return false;
  return Boolean(navigator.geolocation && navigator.geolocation.watchPosition);
}

/**
 * DeviceOrientationEvent exists on most browsers, but iOS Safari requires explicit permission.
 */
export function hasDeviceOrientationEvent() {
  if (!isBrowser()) return false;
  return typeof window.DeviceOrientationEvent !== "undefined";
}

export function canRequestDeviceOrientationPermission() {
  if (!isBrowser()) return false;
  const DOE = window.DeviceOrientationEvent;
  return Boolean(DOE && typeof DOE.requestPermission === "function");
}
